<template>
  <dashboard-template>
    <template slot="dashboard-header">
      <div class="d-flex justify-content-between align-items-center w-100">
        <h6 class="mb-0" v-if="projects.length > 0">{{ $t('projects.select_project') }}</h6>
        <h6 class="mb-0" v-else>{{ $t('projects.no_projects') }}</h6>
        <img class="tooltip-icon icon-small mr-3" id="tooltip-project-select" v-bind:src="'/app/images/icons/info.svg'" />
        <b-popover target="tooltip-project-select" variant="warning" triggers="hover" placement="bottomleft">
          <div v-html="$t('tooltips.project_select')"></div>
        </b-popover>
      </div>
    </template>
    <template slot="dashboard-body">
      <div class="dashboard-content mb-3">
        <div v-if="projects.length > 0" class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <p class="mb-0 mr-4">{{ $t('projects.project_name') }}</p>
            <multiselect
              @select="onProjectSelect"
              class="projects-multiselect"
              label="name"
              track-by="id"
              v-model="selectedProject"
              :allow-empty="false"
              :deselect-label="''"
              :multiple="false"
              :options="projects"
              :searchable="false"
              :select-label="''"
              :selected-label="''">
              <template slot="option" slot-scope="props">
                <span class="option__title">{{ props.option.name }}</span>
                <span v-if="props.option.active" class="option__small text-success">{{ $t('projects.active') }}</span>
                <span v-else class="option__small text-danger">{{ $t('projects.inactive') }}</span>
              </template>
            </multiselect>
          </div>
          <div>
            <router-link to="/project/create">
              <button type="button" class="btn btn-large btn-orange-dark">
                <plus-circle-icon></plus-circle-icon>
                <span>{{ $t('projects.create_project') }}</span>
              </button>
            </router-link>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end my-3">
        <img class="tooltip-icon icon-small mr-3" id="tooltip-project-selected" v-bind:src="'/app/images/icons/info.svg'" />
        <b-popover target="tooltip-project-selected" variant="warning" triggers="hover" placement="bottomleft">
          <div v-html="$t('tooltips.project_selected')"></div>
        </b-popover>
      </div>
      <div v-if="isProjectSelected" class="dashboard-content">
        <project-description-modal @onProjectDescriptionEdit="updateProjectDescription" :currentProject="selectedProject"></project-description-modal>
        <div class="project-summary-row d-flex align-items-center">
          <div class="w-50">
            <h5>
              <span class="text-gray-link">{{$t('projects.chosen')}}</span>
              <router-link :to="{ name: 'project.show', params: { id: this.selectedProject.id }}">
                <span class="project-title text-orange-dark">{{ selectedProject.name }}</span>
              </router-link>
            </h5>
          </div>
          <div class="d-flex align-items-center">
            <p class="mb-0"><strong>{{ $t('projects.project_description') }}</strong><span> {{ selectedProject.description }}</span></p>
          </div>
        </div>
        <div class="project-summary-row d-flex justify-content-around">
          <div class="d-flex">
            <div class="d-flex align-items-center mr-3">
              <img v-if="alertsCriticalCount > 0" class="icon" src="/app/images/icons/warning_danger.svg" />
              <img v-else-if="alertWarningsCount > 0" class="icon" src="/app/images/icons/warning.svg" />
            </div>
            <div>
              <p class="mb-2">{{ $t('projects.feeds_status') }} <strong v-if="selectedProject.active" class="text-green">{{ $t('projects.active') }}</strong><strong v-else class="text-danger">{{ $t('projects.inactive') }}</strong></p>
              <router-link :to="{ name: 'project.show', params: { id: this.selectedProject.id }}">
                <span>{{ $t('projects.actions_required') }} </span><strong v-if="alertsCount === 0" class="text-green">0</strong><strong v-else-if="alertsCriticalCount > 0" class="text-danger"> {{ alertsCount }}</strong><strong v-else-if="alertWarningsCount > 0" class="text-warning">{{ alertWarningsCount }}</strong>
              </router-link>
            </div>
          </div>
          <div class="d-flex flex-column align-items-center justify-content-center">
            <p>{{ $t('projects.last_update') }}</p>
            <p class="text-orange-dark mb-0" v-bind:value="selectedProject.lastUpdate"><strong>{{ selectedProject.lastUpdate | moment('D MMMM YYYY HH:mm') }}</strong></p>
          </div>
          <div class="d-flex flex-column align-items-center">
            <p>{{ $t('projects.next_update') }}</p>
            <p class="text-orange-dark mb-0" v-bind:value="selectedProject.lastUpdate"><strong>{{ countHoursTo(new Date(), nextUpdateDate) }}</strong></p>
          </div>
        </div>
        <div class="project-summary-row d-flex justify-content-around" v-if="selectedProject.sources.length > 0">
          <div class="d-flex flex-column align-items-center">
            <p>{{ $t('projects.amount_of_products') }}</p>
            <router-link :to="{ name: 'products', params: { id: selectedProject.id }}">
              <p class="text-orange-dark mb-0"><strong>{{ selectedProject.productCount }}</strong></p>
            </router-link>
          </div>
          <div class="d-flex flex-column align-items-center">
            <p>{{ $t('projects.data_sources') }}</p>
            <router-link :to="{ name: 'sources', params: { id: selectedProject.id }}">
              <p class="text-orange-dark mb-0"><strong>{{ selectedProject.sources.length }} {{ $t('projects.base_files') }}</strong></p>
            </router-link>
          </div>
          <div class="d-flex flex-column align-items-center">
            <p>{{ $t('global.feeds') }}:</p>
            <router-link :to="{ name: 'feeds', params: { id: selectedProject.id }}">
              <p class="text-orange-dark mb-0 text-lowercase"><strong>{{ selectedProject.feeds.length }} {{ $t('projects.project_show_feeds') }}</strong></p>
            </router-link>
          </div>
        </div>
        <div v-else class="project-summary-row d-flex flex-column w-100">
          <div class="alert alert-danger mb-4 mt-3 w-50" role="alert">
            {{ $t('sources.no_files') }}
          </div>
          <router-link :to="{ name: 'source.create', params: { projectId: this.selectedProject.id }}">
            <button type="button" class="btn btn-large btn-orange mr-2 mt-2">
              <plus-circle-icon></plus-circle-icon>
              <span>{{ $t('projects.project_show_add_source') }}</span>
            </button>
          </router-link>
        </div>
        <div class="project-summary-row d-flex justify-content-between">
          <div>
            <button type="button" class="btn btn-large btn-orange-dark" @click="$bvModal.show('projectDescriptionModal')">
              <edit-icon></edit-icon>
              <span>{{ $t('projects.edit_project_description') }}</span>
            </button>
          </div>
          <div class="d-flex">
            <button v-if="selectedProject.active" type="button" class="btn btn-medium btn-gray-dark" @click="updateProjectStatus">
              <pause-circle-icon></pause-circle-icon>
              <span>{{ $t('projects.deactivate') }}</span>
            </button>
            <button v-else type="button" class="btn btn-medium btn-orange-dark" @click="updateProjectStatus">
              <play-circle-icon></play-circle-icon>
              <span>{{ $t('projects.activate') }}</span>
            </button>
            <router-link :to="{ name: 'project.show', params: { id: this.selectedProject.id }}" class="ml-4">
              <button type="button" class="btn btn-medium btn-orange">
                <eye-icon></eye-icon>
                <span>{{ $t('projects.project_view') }}</span>
              </button>
            </router-link>
          </div>
        </div>
      </div>
    </template>
  </dashboard-template>
</template>

<script>
import axios from 'axios'
import Multiselect from 'vue-multiselect'
import DashboardTemplate from '@/views/Dashboard'
import ProjectDescriptionModal from './Partials/ProjectDescriptionModal'
import { EditIcon, EyeIcon, PauseCircleIcon, PlayCircleIcon, PlusCircleIcon } from 'vue-feather-icons'
import dates from '@/mixins/dates'

export default {
  name: 'Projects',

  data () {
    return {
      availableProjects: this.projects,
      isProjectSelected: false,
      selectedProject: this.project
    }
  },

  props: {
    projects: { required: false, type: Array },
    project: { required: false, type: Object }
  },

  mixins: [dates],

  created () {
    if (Object.keys(this.selectedProject).length !== 0) {
      this.isProjectSelected = true
    } else this.isProjectSelected = false
  },

  mounted () {
    if (this.availableProjects.length === 0) {
      this.$router.push({ name: 'project.create' })
    }
  },

  methods: {
    onProjectSelect (project) {
      this.$emit('onProjectSelect', project.id)
    },

    updateProjectStatus () {
      let status = true
      if (this.selectedProject.active) status = false
      axios.post(process.env.VUE_APP_API_ENDPOINT + '/data/projects/update/' + this.selectedProject.id, {
        name: this.selectedProject.name,
        active: status,
        description: this.selectedProject.description
      })
        .then((response) => {
          if ((response.status === 201) || (response.status === 200)) {
            this.selectedProject.active = status
            if (this.selectedProject.active) {
              this.$bvToast.toast(this.$t('projects.project_activate_description'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('projects.project_activate_title'),
                variant: 'success'
              })
            } else {
              this.$bvToast.toast(this.$t('projects.project_deactivate_description'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('projects.project_deactivate_title'),
                variant: 'success'
              })
            }
          }
        })
        .catch((error) => {
          if (error.response) {
            if (status) {
              this.$bvToast.toast(this.$t('projects.project_activate_error_description'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('projects.project_activate_error_title'),
                variant: 'danger'
              })
            } else {
              this.$bvToast.toast(this.$t('projects.project_deactivate_error_description'), {
                autoHideDelay: 3000,
                solid: true,
                title: this.$t('projects.project_deactivate_error_title'),
                variant: 'danger'
              })
            }
          }
        })
    },

    updateProjectDescription (value) {
      this.selectedProject.description = value
    }
  },

  computed: {
    alertsCount () {
      return this.project.alerts.source.length + this.project.alerts.feed.length + this.project.alerts.project.length
    },

    alertsCriticalCount () {
      return this.project.alerts.feed.filter(alert => alert.level === 0).length + this.project.alerts.source.filter(alert => alert.level === 0).length + this.project.alerts.project.filter(alert => alert.level === 0).length
    },

    alertWarningsCount () {
      return this.project.alerts.feed.filter(alert => alert.level === 1).length + this.project.alerts.source.filter(alert => alert.level === 1).length + this.project.alerts.project.filter(alert => alert.level === 1).length
    },

    nextUpdateDate () {
      if (this.project.lastUpdate !== undefined) return this.$moment(this.project.lastUpdate).add(1, 'days')
      else return new Date()
    }
  },

  components: { DashboardTemplate, EditIcon, EyeIcon, Multiselect, PauseCircleIcon, PlayCircleIcon, PlusCircleIcon, ProjectDescriptionModal }
}
</script>
